import React, { useState } from 'react';
import { SendIcon, SmartphoneIcon, CheckCircle2Icon, ClipboardListIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
const CREW_MEMBERS = [{
  name: 'Luis',
  role: 'Rigger'
}, {
  name: 'Jake',
  role: 'Ironworker'
}, {
  name: 'Dan',
  role: 'Crane Operator'
}];
export function Screen18_SendQuiz({
  onNext
}: ScreenProps) {
  const [sending, setSending] = useState(false);
  const [sentMembers, setSentMembers] = useState<Set<string>>(new Set());
  const allSent = sentMembers.size === CREW_MEMBERS.length;
  const handleSend = () => {
    setSending(true);
    CREW_MEMBERS.forEach((member, i) => {
      setTimeout(() => {
        setSentMembers(prev => new Set(prev).add(member.name));
      }, 800 + i * 700);
    });
  };
  return <div className="flex flex-col h-full" data-id="element-882">
      <div className="mb-5" data-id="element-883">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-884">
          Crew Understanding Quiz
        </h2>
        <p className="text-slate-400 text-sm" data-id="element-885">
          Send a short safety quiz to verify each crew member understands the
          PTP before work begins.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-886">
        {/* Quiz Info Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-id="element-887">
          <div className="flex items-center gap-3" data-id="element-888">
            <ClipboardListIcon className="w-5 h-5 text-orange-500 flex-shrink-0" data-id="element-889" />
            <div data-id="element-890">
              <p className="text-white font-semibold text-sm" data-id="element-891">
                PTP Safety Quiz
              </p>
              <p className="text-slate-400 text-xs" data-id="element-892">
                3 questions · Based on today's PTP
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-3 space-y-2" data-id="element-893">
            <div className="flex items-center justify-between" data-id="element-894">
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-895">
                Task
              </span>
              <span className="text-orange-400 text-xs font-bold" data-id="element-896">
                Steel Beam Installation
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-897">
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-898">
                Questions
              </span>
              <span className="text-white text-xs font-medium" data-id="element-899">
                Hazards, controls, emergency response
              </span>
            </div>
          </div>
        </div>

        {/* Crew Members to Receive Quiz */}
        <section data-id="element-900">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-901">
            Send Quiz To
          </h3>
          <div className="space-y-2" data-id="element-902">
            {CREW_MEMBERS.map(member => <div key={member.name} className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-903">
                <SmartphoneIcon className="w-4 h-4 text-slate-500 flex-shrink-0" data-id="element-904" />
                <div className="flex-1" data-id="element-905">
                  <span className="text-white font-medium text-sm" data-id="element-906">
                    {member.name}
                  </span>
                  <span className="text-slate-500 text-xs ml-2" data-id="element-907">
                    · {member.role}
                  </span>
                </div>
                <AnimatePresence mode="wait" data-id="element-908">
                  {sentMembers.has(member.name) ? <motion.div key="sent" initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 15
              }} data-id="element-909">
                      <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-910" />
                    </motion.div> : sending ? <motion.div key="sending" animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-5 h-5 rounded-full border-2 border-slate-700 border-t-orange-500" data-id="element-911" /> : <motion.div key="pending" className="w-2 h-2 rounded-full bg-slate-600" data-id="element-912" />}
                </AnimatePresence>
              </div>)}
          </div>
        </section>

        {/* Foreman Note */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-id="element-913">
          <p className="text-orange-100 text-sm leading-relaxed" data-id="element-914">
            <span className="font-bold text-white" data-id="element-915">Note:</span> After sending
            the quiz to the crew, you will also complete the quiz yourself as
            the foreman.
          </p>
        </div>

        {/* Sent Confirmation */}
        <AnimatePresence data-id="element-916">
          {allSent && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.3
        }} className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3" data-id="element-917">
              <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-918" />
              <div data-id="element-919">
                <p className="text-green-300 font-semibold text-sm" data-id="element-920">
                  Quiz sent to {CREW_MEMBERS.length} crew members
                </p>
                <p className="text-green-200/60 text-xs mt-0.5" data-id="element-921">
                  Workers will receive the quiz on their devices.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-922">
        {!sending ? <PrimaryButton onClick={handleSend} icon={<SendIcon className="w-5 h-5" data-id="element-924" />} data-id="element-923">
            Send Quiz to Crew
          </PrimaryButton> : <PrimaryButton onClick={onNext} disabled={!allSent} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-926" />} data-id="element-925">
            {allSent ? 'Take Quiz as Foreman' : `Sending quiz… (${sentMembers.size}/${CREW_MEMBERS.length})`}
          </PrimaryButton>}
      </div>
    </div>;
}